<script setup lang="ts">
/**
 * EveSyncDialog — Import from / Export to EVE character
 *
 * Props:
 *   visible — controls dialog visibility
 *
 * Events:
 *   close — when dialog should close
 *   exported — after successful export to EVE
 *   synced — after successful sync from EVE
 */
import { ref, watch } from 'vue';
import { useAuth } from '../../composables/useAuth';
import { useFitStore } from '../../stores/fitStore';
import { syncPersonalFittings, exportToEve } from '../../composables/useApi';

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  close: [];
  exported: [];
  synced: [];
}>();

const { isLoggedIn, characterName } = useAuth();
const store = useFitStore();

const syncing = ref(false);
const exporting = ref(false);

watch(() => props.visible, (v) => {
  if (v) {
    syncing.value = false;
    exporting.value = false;
  }
});

async function handleSync(): Promise<void> {
  syncing.value = true;
  try {
    await syncPersonalFittings();
    store.addToast('success', 'Fittings synced from EVE');
    emit('synced');
  } catch {
    // Error shown by useApi
  } finally {
    syncing.value = false;
  }
}

async function handleExport(): Promise<void> {
  const fitID = store.activeTab?.fitID;
  if (!fitID) {
    store.addToast('warning', 'No fit open to export');
    return;
  }

  exporting.value = true;
  try {
    await exportToEve(fitID);
    store.addToast('success', 'Fitting exported to EVE character');
    emit('exported');
  } catch {
    // Error shown by useApi
  } finally {
    exporting.value = false;
  }
}

function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    emit('close');
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
    <div v-if="visible" class="modal-overlay" @click.self="emit('close')" @keydown="handleKeydown">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3 class="modal-title">EVE Sync</h3>
          <button class="modal-close" @click="emit('close')">&times;</button>
        </div>

        <div class="modal-body">
          <template v-if="!isLoggedIn">
            <p class="sync-message">You need to be logged in with EVE SSO to sync fittings.</p>
          </template>

          <template v-else>
            <p class="sync-char">Logged in as <strong>{{ characterName }}</strong></p>

            <div class="sync-actions">
              <!-- Import from EVE -->
              <div class="sync-action">
                <div class="sync-action-info">
                  <span class="sync-action-title">Import from EVE</span>
                  <span class="sync-action-desc">Sync your in-game fittings to EveFit</span>
                </div>
                <button
                  class="btn btn--primary"
                  :disabled="syncing"
                  @click="handleSync"
                >
                  {{ syncing ? 'Syncing...' : 'Sync Fittings' }}
                </button>
              </div>

              <!-- Export to EVE -->
              <div class="sync-action">
                <div class="sync-action-info">
                  <span class="sync-action-title">Export to EVE</span>
                  <span class="sync-action-desc">
                    Send "{{ store.activeFit?.fitName || 'current fit' }}" to your EVE character
                  </span>
                </div>
                <button
                  class="btn btn--primary"
                  :disabled="exporting || !store.activeTab?.fitID"
                  @click="handleExport"
                >
                  {{ exporting ? 'Exporting...' : 'Export Fit' }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <button class="btn btn--secondary" @click="emit('close')">Close</button>
        </div>
      </div>
    </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-dialog {
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 440px;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  animation: modal-in 0.15s ease-out;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.modal-close:hover {
  color: var(--text-primary);
}

.modal-body {
  padding: 16px;
}

.sync-message {
  font-size: 0.85rem;
  color: var(--text-secondary);
  text-align: center;
  margin: 0;
  padding: 16px 0;
}

.sync-char {
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin: 0 0 16px;
}

.sync-char strong {
  color: var(--text-primary);
}

.sync-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sync-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  background: var(--bg-darker);
}

.sync-action-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.sync-action-title {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-primary);
}

.sync-action-desc {
  font-size: 0.72rem;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
}

.btn {
  padding: 6px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.btn--secondary {
  background: transparent;
  color: var(--text-secondary);
}

.btn--secondary:hover {
  color: var(--text-primary);
  border-color: var(--text-secondary);
}

.btn--primary {
  background: var(--accent-blue);
  border-color: var(--accent-blue);
  color: #fff;
}

.btn--primary:hover:not(:disabled) {
  background: #3a8eef;
}

.btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal transitions */
.modal-enter-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal-dialog {
  animation: modal-in 0.2s ease-out;
}

.modal-leave-active {
  transition: opacity 0.15s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

@keyframes modal-in {
  from {
    transform: translateY(-8px) scale(0.97);
  }
  to {
    transform: translateY(0) scale(1);
  }
}
</style>
